import * as React from 'react';
import { ImSpinner2 } from 'react-icons/im';

import clsxm from '@/lib/clsxm';

enum ButtonVariant {
  'primary',
  'outline',
  'ghost',
  'light',
  'dark',
  'simple',
  'plain',
}

type ButtonProps = {
  isLoading?: boolean;
  isDarkBg?: boolean;
  variant?: keyof typeof ButtonVariant;
} & React.ComponentPropsWithRef<'button'>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      disabled: buttonDisabled,
      isLoading,
      variant = 'primary',
      isDarkBg = false,
      ...rest
    },
    ref,
  ) => {
    const disabled = isLoading || buttonDisabled;

    return (
      <button
        ref={ref}
        type='button'
        disabled={disabled}
        className={clsxm(
          'inline-flex items-center rounded-md px-4 py-2 font-medium',
          'focus:outline-none focus-visible:ring focus-visible:ring-primary-500',
          'shadow-sm sm:text-sm',
          'transition-colors duration-75',
          //#region  //*=========== Variants ===========
          [
            variant === 'primary' && [
              'bg-primary-600 text-white',
              'border border-transparent',
              'hover:bg-primary-700 hover:text-white',
              'active:bg-primary-500',
              'disabled:bg-primary-400 disabled:hover:bg-primary-400',
            ],
            variant === 'plain' && [
              ' shadow-none   hover:bg-gray-100 disabled:hover:bg-transparent dark:hover:bg-gray-900 dark:hover:text-gray-400 dark:disabled:hover:bg-transparent outline-none',
            ],
            variant === 'outline' && [
              'text-primary-500',
              'border border-primary-500',
              'hover:bg-primary-50 active:bg-primary-100 disabled:bg-primary-100',
              'dark:hover:bg-gray-900 dark:active:bg-gray-800 dark:disabled:bg-gray-800',
            ],
            variant === 'ghost' && [
              'inline rounded-none py-1 px-0 text-primary-500',
              'animated-underline custom-link shadow-none',
              'focus:outline-none focus-visible:ring focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            ],
            variant === 'simple' && [
              'inline-flex items-center py-0.5 px-2 text-gray-900 font-medium dark:text-slate-400',
              //'border border-primary-500',
              'border border-gray-300 dark:border-gray-700',
              'dark:hover:text-primary-500 hover:border-primary-500 dark:hover:border-primary-500 active:bg-primary-100 ',
              'custom-link shadow-none hover:text-primary-600',
              'focus:outline-none focus-visible:ring disabled:text-gray-400 disabled:border-gray-200 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            ],
            variant === 'light' && [
              'bg-white text-dark dark:bg-slate-800 dark:text-slate-400',
              'border border-gray-300 dark:border-gray-700',
              'hover:bg-gray-100 hover:text-dark dark:hover:bg-gray-700 dark:hover:text-white',
              'active:bg-white/80 disabled:bg-gray-200 disabled:hover:bg-gray-200',
            ],
            variant === 'dark' && [
              'bg-gradient-to-r from-purple-600 to-indigo-600 text-white',
              'border border-transparent bg-origin-border text-base',
              'hover:from-purple-700 hover:to-indigo-700',
              'active:bg-primary-500',
              'disabled:opacity-90 disabled:hover:opacity-100',
            ],
          ],
          //#endregion  //*======== Variants ===========
          'disabled:cursor-not-allowed',
          isLoading &&
            'relative text-transparent transition-none hover:text-transparent disabled:cursor-wait',
          className,
        )}
        {...rest}
      >
        {isLoading && (
          <div
            className={clsxm(
              'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
              {
                'text-white': ['primary', 'dark'].includes(variant),
                'text-black': ['light'].includes(variant),
                'text-primary-500': ['outline', 'ghost', 'simple'].includes(
                  variant,
                ),
                'left-0 -translate-x-0': ['simple'].includes(variant),
              },
            )}
          >
            <ImSpinner2 className='animate-spin' />
          </div>
        )}
        {children}
      </button>
    );
  },
);

export default Button;
